import { useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { DateField } from '@/components/forms/DateField';
import { toLocalISODate } from '@/utils/date-utils';

interface SnoozeSheetProps {
  onSnooze: (until: Date) => void;
  onClose: () => void;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function toLocalDateString(date: Date): string {
  return toLocalISODate(date);
}

const PRESETS = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
  { label: 'Next month', days: 30 },
] as const;

export function SnoozeSheet({ onSnooze, onClose }: SnoozeSheetProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minDate = toLocalDateString(addDays(today, 1));
  const [customDate, setCustomDate] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  function handlePreset(days: number) {
    onSnooze(addDays(today, days));
  }

  function handleCustomSubmit() {
    if (!customDate) return;
    const date = new Date(customDate + 'T00:00:00');
    onSnooze(date);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
        data-testid="snooze-backdrop"
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--color-elevated)] border-t border-[var(--color-border)] rounded-t-2xl shadow-card-lg p-6 flex flex-col gap-4 max-h-[80vh] overflow-y-auto"
        data-testid="snooze-sheet"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-display-sm text-[var(--color-text-display)]">
            Snooze Task
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close snooze picker"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {PRESETS.map(({ label, days }) => (
            <button
              key={label}
              type="button"
              onClick={() => handlePreset(days)}
              data-testid={`snooze-preset-${days}`}
              className="w-full text-left px-4 py-3 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] hover:border-[var(--color-border-subtle)] transition-colors duration-100"
            >
              {label}
            </button>
          ))}

          <button
            type="button"
            onClick={() => setShowCustom((v) => !v)}
            data-testid="snooze-custom-toggle"
            className="w-full text-left px-4 py-3 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] hover:border-[var(--color-border-subtle)] transition-colors duration-100"
          >
            Custom date…
          </button>
        </div>

        {showCustom && (
          <div className="flex flex-col gap-3" data-testid="snooze-custom-picker">
            <DateField
              id="snooze-date-input"
              value={customDate}
              min={minDate}
              onChange={setCustomDate}
            />
            <Button
              variant="primary"
              onClick={handleCustomSubmit}
              disabled={!customDate}
              data-testid="snooze-custom-submit"
            >
              Snooze until this date
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
